import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Sparkles, Building2, Palette, User, FolderTree, Map, Calendar, UserPlus,
  ChevronLeft, ChevronRight, Check, CheckCircle2, X, Upload, SkipForward,
} from 'lucide-react';

const STEPS = [
  { i: 1, label: 'Welcome',     icon: Sparkles  },
  { i: 2, label: 'Company',     icon: Building2 },
  { i: 3, label: 'Branding',    icon: Palette   },
  { i: 4, label: 'Your profile', icon: User     },
  { i: 5, label: 'Departments', icon: FolderTree },
  { i: 6, label: 'Branches',    icon: Map       },
  { i: 7, label: 'Policies',    icon: Calendar  },
  { i: 8, label: 'Invite team', icon: UserPlus  },
];

export default function SetupWizard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  // step payloads
  const [identity, setIdentity] = useState({});
  const [branding, setBranding] = useState({ primary_color: '#3D4A8E', logo_url: '' });
  const [admin, setAdmin] = useState({ name: '', phone: '', title: '' });
  const [departments, setDepts] = useState([{ name: 'Engineering' }, { name: 'HR' }, { name: 'Sales' }]);
  const [branches, setBranches] = useState([{ name: 'HQ', city: '', state: '', country: 'India' }]);
  const [policies, setPolicies] = useState({ casual_leaves: 10, sick_leaves: 5, probation_months: 3, weekly_hours: 40 });
  const [invites, setInvites] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/setup/status');
        setStatus(data);
        if (data.setup_completed) { nav('/dashboard'); return; }
        setStep(data.setup_step || 1);
        setIdentity({
          company_name: data.company_name || '', company_domain: data.company_domain || '',
          company_type: data.company_type || '', industry: data.industry || '',
          company_address: data.company_address || '', time_zone: data.time_zone || 'Asia/Kolkata',
        });
        setBranding(data.branding || { primary_color: '#3D4A8E' });
        setAdmin({ name: user?.name || '', phone: user?.phone || '', title: user?.title || '' });
      } catch (e) { /* unauth */ }
    })();
  }, []); // eslint-disable-line

  const saveStep = async (skip = false) => {
    setBusy(true);
    try {
      const dataByStep = { 1: {}, 2: identity, 3: branding, 4: admin,
        5: { departments }, 6: { branches }, 7: policies,
        8: { invites: invites.split(/[\n,]+/).map(s => s.trim()).filter(Boolean).map(e => ({ email: e, role: 'employee' })) } };
      await api.post('/setup/step', { step, data: dataByStep[step] || {}, skip });
      if (step < STEPS.length) setStep(step + 1);
      else await finish();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.post('/setup/finish');
      toast.success('Setup complete — welcome!');
      await refresh?.();
      nav('/dashboard');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-6" data-testid="setup-wizard">
      <div className="max-w-4xl mx-auto">
        {/* Progress strip */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6">
          <div className="flex items-center justify-between text-xs">
            {STEPS.map(s => {
              const done = s.i < step, active = s.i === step;
              const Icon = s.icon;
              return (
                <div key={s.i} className="flex-1 flex flex-col items-center" data-testid={`step-${s.i}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-1 ${
                    done ? 'bg-emerald-500 text-white' :
                    active ? 'bg-gradient-to-r from-amber-500 to-rose-500 text-white' :
                    'bg-slate-100 text-slate-400'
                  }`}>
                    {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${
                    active ? 'text-slate-900' : done ? 'text-emerald-700' : 'text-slate-400'
                  }`}>{s.label}</div>
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-slate-400 text-center mt-2">Step {step} of {STEPS.length}</div>
        </div>

        {/* Step content */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm" data-testid={`step-content-${step}`}>
          {step === 1 && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-rose-500 text-white flex items-center justify-center mb-4"><Sparkles className="w-8 h-8" /></div>
              <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">Welcome to your HRMS!</h1>
              <p className="text-slate-500 mt-3 max-w-md mx-auto">We&apos;ll guide you through 8 quick steps to set up your tenant. You can skip any step and come back later — your progress is saved automatically.</p>
            </div>
          )}

          {step === 2 && (
            <SectionWrap title="Company identity" subtitle="Tell us about your organisation. We&apos;ve pre-filled from your sign-up.">
              <Field label="Company name *"><Input value={identity.company_name} onChange={(e) => setIdentity({ ...identity, company_name: e.target.value })} data-testid="i-name" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Primary domain"><Input value={identity.company_domain} onChange={(e) => setIdentity({ ...identity, company_domain: e.target.value })} data-testid="i-domain" placeholder="acme.com" /></Field>
                <Field label="Industry"><Input value={identity.industry} onChange={(e) => setIdentity({ ...identity, industry: e.target.value })} data-testid="i-industry" /></Field>
              </div>
              <Field label="Company type"><Input value={identity.company_type} onChange={(e) => setIdentity({ ...identity, company_type: e.target.value })} data-testid="i-type" placeholder="Private Limited" /></Field>
              <Field label="Address"><Textarea rows={3} value={identity.company_address} onChange={(e) => setIdentity({ ...identity, company_address: e.target.value })} data-testid="i-address" /></Field>
            </SectionWrap>
          )}

          {step === 3 && (
            <SectionWrap title="Branding" subtitle="Pick a primary colour and upload your logo — these will theme the app for everyone in your tenant.">
              <div className="grid grid-cols-2 gap-6 items-start">
                <div>
                  <Field label="Primary brand colour">
                    <div className="flex items-center gap-3">
                      <input type="color" value={branding.primary_color || '#3D4A8E'} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="w-14 h-14 rounded-lg cursor-pointer" data-testid="b-color" />
                      <code className="text-xs font-mono bg-slate-100 px-2 py-1 rounded">{branding.primary_color}</code>
                    </div>
                  </Field>
                  <Field label="Logo (PNG / SVG, ≤500 KB)">
                    <label className="block border-2 border-dashed border-slate-300 rounded-xl p-4 cursor-pointer hover:border-amber-400 transition">
                      <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                      <div className="text-xs text-slate-500 text-center">Click to upload</div>
                      <input type="file" className="hidden" accept="image/png,image/jpeg,image/svg+xml" onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        if (f.size > 500_000) { toast.error('Logo must be ≤500 KB'); return; }
                        const reader = new FileReader();
                        reader.onload = () => setBranding({ ...branding, logo_url: reader.result });
                        reader.readAsDataURL(f);
                      }} data-testid="b-logo-input" />
                    </label>
                  </Field>
                </div>
                <div className="border border-slate-200 rounded-2xl p-6 bg-slate-50">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-3">Live preview</div>
                  <div className="rounded-xl p-4" style={{ backgroundColor: branding.primary_color }}>
                    <div className="flex items-center gap-2 text-white">
                      {branding.logo_url
                        ? <img src={branding.logo_url} alt="" className="w-8 h-8 object-contain rounded bg-white/10 p-0.5" />
                        : <div className="w-8 h-8 rounded bg-white/20 flex items-center justify-center text-xs font-bold">{(identity.company_name || 'C').charAt(0)}</div>}
                      <div className="font-bold text-sm">{identity.company_name || 'Your Company'}</div>
                    </div>
                  </div>
                  <Button style={{ backgroundColor: branding.primary_color }} className="text-white mt-3 w-full rounded-full">Primary button</Button>
                </div>
              </div>
            </SectionWrap>
          )}

          {step === 4 && (
            <SectionWrap title="Your profile" subtitle="Confirm your name and contact details — these appear on letters you sign.">
              <Field label="Full name *"><Input value={admin.name} onChange={(e) => setAdmin({ ...admin, name: e.target.value })} data-testid="a-name" /></Field>
              <Field label="Designation"><Input value={admin.title} onChange={(e) => setAdmin({ ...admin, title: e.target.value })} data-testid="a-title" placeholder="Head of HR" /></Field>
              <Field label="Phone"><Input value={admin.phone} onChange={(e) => setAdmin({ ...admin, phone: e.target.value })} data-testid="a-phone" /></Field>
            </SectionWrap>
          )}

          {step === 5 && (
            <SectionWrap title="Departments" subtitle="Add 3–5 starter departments — you can change them anytime later.">
              <RepeatList items={departments} setItems={setDepts} fields={[{ k: 'name', placeholder: 'Department name' }]} addLabel="Add department" testid="depts" />
            </SectionWrap>
          )}

          {step === 6 && (
            <SectionWrap title="Branches" subtitle="At least one branch is recommended.">
              <RepeatList items={branches} setItems={setBranches} fields={[
                { k: 'name', placeholder: 'Branch name' },
                { k: 'city', placeholder: 'City' },
                { k: 'state', placeholder: 'State' },
              ]} addLabel="Add branch" testid="branches" />
            </SectionWrap>
          )}

          {step === 7 && (
            <SectionWrap title="Default leave & work policies" subtitle="These become the defaults for every new offer letter.">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Casual leaves / year"><Input type="number" value={policies.casual_leaves} onChange={(e) => setPolicies({ ...policies, casual_leaves: e.target.value })} data-testid="p-cl" /></Field>
                <Field label="Sick leaves / year"><Input type="number" value={policies.sick_leaves} onChange={(e) => setPolicies({ ...policies, sick_leaves: e.target.value })} data-testid="p-sl" /></Field>
                <Field label="Probation (months)"><Input type="number" value={policies.probation_months} onChange={(e) => setPolicies({ ...policies, probation_months: e.target.value })} data-testid="p-prob" /></Field>
                <Field label="Weekly hours"><Input type="number" value={policies.weekly_hours} onChange={(e) => setPolicies({ ...policies, weekly_hours: e.target.value })} data-testid="p-hrs" /></Field>
              </div>
            </SectionWrap>
          )}

          {step === 8 && (
            <SectionWrap title="Invite your team" subtitle="Optional. Paste emails (comma- or newline-separated). They&apos;ll appear in your pending-invite list.">
              <Field label="Team emails">
                <Textarea rows={6} value={invites} onChange={(e) => setInvites(e.target.value)} placeholder="jane@acme.com, dev@acme.com" data-testid="invite-emails" />
              </Field>
              <p className="text-xs text-slate-500">You&apos;ll be able to send actual invitations from the Employees page.</p>
            </SectionWrap>
          )}

          {/* Action bar */}
          <div className="mt-8 flex items-center justify-between border-t border-slate-100 pt-5">
            <Button variant="ghost" disabled={step === 1 || busy} onClick={() => setStep(step - 1)} data-testid="prev-btn">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex gap-2">
              {step > 1 && step < STEPS.length && (
                <Button variant="outline" onClick={() => saveStep(true)} disabled={busy} data-testid="skip-btn" className="rounded-full">
                  <SkipForward className="w-4 h-4 mr-1.5" /> Skip
                </Button>
              )}
              <Button onClick={() => saveStep(false)} disabled={busy} data-testid="next-btn"
                      className="rounded-full bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-600 hover:to-rose-600 text-white px-6">
                {step === STEPS.length ? (busy ? 'Finishing…' : 'Finish setup') : (busy ? 'Saving…' : 'Continue')}
                {step !== STEPS.length && <ChevronRight className="w-4 h-4 ml-1" />}
                {step === STEPS.length && <CheckCircle2 className="w-4 h-4 ml-1.5" />}
              </Button>
            </div>
          </div>
        </div>

        <div className="text-center mt-4 text-xs text-slate-400">
          You can finish this wizard later — we&apos;ll remember where you stopped.
        </div>
      </div>
    </div>
  );
}

function SectionWrap({ title, subtitle, children }) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">{title}</h2>
        <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function RepeatList({ items, setItems, fields, addLabel, testid }) {
  const update = (idx, k, v) => { const a = [...items]; a[idx] = { ...a[idx], [k]: v }; setItems(a); };
  const add = () => setItems([...items, fields.reduce((o, f) => ({ ...o, [f.k]: '' }), {})]);
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  return (
    <div data-testid={testid} className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          {fields.map(f => (
            <Input key={f.k} value={it[f.k] || ''} placeholder={f.placeholder}
                   onChange={(e) => update(i, f.k, e.target.value)}
                   data-testid={`${testid}-${i}-${f.k}`} className="flex-1" />
          ))}
          {items.length > 1 && <Button variant="ghost" size="sm" onClick={() => remove(i)}><X className="w-4 h-4" /></Button>}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={add} className="rounded-full" data-testid={`${testid}-add`}>+ {addLabel}</Button>
    </div>
  );
}
